import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionStatus, AccountInfo } from '@azure/msal-browser';
import { useCallback } from 'react';
import jwtTokenPayload from './tokenPayload.json';
import { isAuthMocked } from '../config/config';
import { useConfigContext } from '../config/configContext';

export interface AuthState {
	initialized: boolean;
	isAuthenticated: boolean;
	account: AccountInfo | null;
	login: () => Promise<void>;
	logout: () => Promise<void>;
	getAccessToken: () => Promise<string | null>;
}

interface MockTokenPayload {
	roles?: string[];
	tid?: string;
	[key: string]: unknown;
}

// Browser-compatible mock JWT creator (for testing only)
const createMockJwt = (payload: MockTokenPayload): string => {
	const header = { alg: 'HS256', typ: 'JWT' };
	const now = Math.floor(Date.now() / 1000);
	const tokenPayload = {
		...payload,
		iat: now,
		exp: now + 86400, // 24 hours
	};

	const base64UrlEncode = (obj: object): string => {
		const json = JSON.stringify(obj);
		const base64 = btoa(json);
		return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
	};

	const headerEncoded = base64UrlEncode(header);
	const payloadEncoded = base64UrlEncode(tokenPayload);
	const signature = base64UrlEncode({ mock: 'signature' });

	return `${headerEncoded}.${payloadEncoded}.${signature}`;
};

const changeToken = () => {
	const data: MockTokenPayload = { ...jwtTokenPayload };
	data.roles =
		window.localStorage.getItem('mockedRoles')?.split(',') || data.roles;
	const mockedTid = window.localStorage.getItem('mockedTid');
	if (mockedTid === 'NOT_DEFINED') {
		delete data.tid;
	} else {
		data.tid = mockedTid ?? data.tid;
	}
	return createMockJwt(data);
};

let mockedToken = isAuthMocked ? changeToken() : '';

const mockedAuth: AuthState = {
	initialized: true,
	isAuthenticated: true,
	account: {
		homeAccountId: 'b5839965-430f-4be2-b282-d7a3149f2b37',
		environment: 'mock',
		tenantId: jwtTokenPayload.tid,
		username: 'User.Test@ysoft.com',
		localAccountId: 'mock-local-account-id',
		name: 'User Test',
	},
	login: async () => {
		return;
	},
	logout: async () => {
		return;
	},
	getAccessToken: async () => {
		return mockedToken;
	},
};

if (isAuthMocked) {
	window.onstorage = () => {
		mockedToken = changeToken();
	};
}

const useAuth = (): AuthState => {
	const { config } = useConfigContext();

	if (isAuthMocked) {
		return mockedAuth;
	}

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const { instance, inProgress, accounts } = useMsal();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const account = useAccount(accounts[0] || null);

	const initialized = inProgress === InteractionStatus.None;
	const isAuthenticated = !!account;

	const login = async () => {
		try {
			await instance.loginRedirect({
				scopes: config.msal.scopes,
			});
		} catch (error) {
			console.error('Login error:', error);
		}
	};

	const logout = async () => {
		try {
			await instance.logoutRedirect({
				postLogoutRedirectUri: config.msal.redirectUri,
			});
		} catch (error) {
			console.error('Logout error:', error);
		}
	};

	// useCallback prevents a new function reference on every render, which would cause
	// userContext's effect (which lists getAccessToken as a dependency) to re-run
	// continuously and trigger repeated acquireTokenSilent calls.
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const getAccessToken = useCallback(async (): Promise<string | null> => {
		const activeAccount = instance.getActiveAccount() || instance.getAllAccounts()[0];

		if (!activeAccount) {
			console.warn('No active account found for token acquisition');
			return null;
		}

		try {
			const response = await instance.acquireTokenSilent({
				scopes: config.msal.scopes,
				account: activeAccount,
			});
			return response.accessToken;
		} catch (error) {
			console.error('Token acquisition error:', error);
			if ((error as { errorCode?: string })?.errorCode === 'endpoints_resolution_error') {
				// The ACQUIRE_TOKEN_FAILURE event in authProvider clears the cache and reloads.
				return null;
			}
			// If silent acquisition fails, try interactive
			try {
				await instance.acquireTokenRedirect({
					scopes: config.msal.scopes,
				});
				return null;
			} catch (interactiveError) {
				console.error('Interactive token acquisition error:', interactiveError);
				return null;
			}
		}
	}, [instance, config.msal.scopes]);

	return {
		initialized,
		isAuthenticated,
		account: account || null,
		login,
		logout,
		getAccessToken,
	};
};

export default useAuth;
